/**
 * @internal
 */
import React, { forwardRef, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Transitions } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { AnimatePresence, m as motion } from 'framer-motion';
import { hasPassiveSupport } from '@polestar/component-warehouse-react/helpers/supports';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useCallbackRef from '@polestar/component-warehouse-react/hooks/useCallbackRef';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
export var BackdropVariants;
(function (BackdropVariants) {
    BackdropVariants["default"] = "default";
    BackdropVariants["medium"] = "medium";
    BackdropVariants["dark"] = "dark";
})(BackdropVariants || (BackdropVariants = {}));
const Backdrop = forwardRef(({ visible = false, onClose, withBackground = true, allowScroll = false, zIndexBaseValue = LayoutIndexes.modal, variant = BackdropVariants.default, withoutModal = false, inset = undefined, ...props }, ref) => {
    const [backdropRef, backdropElement] = useCallbackRef();
    const combinedRef = useCombinedRef(ref, backdropRef);
    const handleClose = useCallback(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    const onKeyPress = useCallback((event) => {
        const key = event.key || event.keyCode;
        switch (key) {
            case 'Escape':
            case 'Esc':
            case 27:
                handleClose();
                break;
        }
    }, [handleClose]);
    useEffect(() => {
        if (visible) {
            window.addEventListener('keydown', onKeyPress);
            return () => {
                window.removeEventListener('keydown', onKeyPress);
            };
        }
    }, [visible, onKeyPress]);
    useEffect(() => {
        if (visible && !allowScroll) {
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;
            const handleScroll = (e) => {
                preventCancelableDefault(e);
                window.scrollTo(scrollX, scrollY);
            };
            window.addEventListener('scroll', handleScroll);
            // This stop the page from jumping around in Chrome
            backdropElement === null || backdropElement === void 0 ? void 0 : backdropElement.addEventListener('wheel', handleScroll, hasPassiveSupport ? { passive: false } : false);
            // On touch devices this prevents the scroll happening below the backdrop
            backdropElement === null || backdropElement === void 0 ? void 0 : backdropElement.addEventListener('touchmove', handleScroll, hasPassiveSupport ? { passive: false } : false);
            return () => {
                window.removeEventListener('scroll', handleScroll);
                backdropElement === null || backdropElement === void 0 ? void 0 : backdropElement.removeEventListener('wheel', handleScroll);
                backdropElement === null || backdropElement === void 0 ? void 0 : backdropElement.removeEventListener('touchmove', handleScroll);
            };
        }
    }, [allowScroll, visible, backdropElement]);
    const uniqueId = `backdrop-${visible}-${zIndexBaseValue}-${withBackground}-${inset === undefined}-${withoutModal}-${variant}`;
    return (React.createElement(AnimatePresence, null, visible && (React.createElement(Container, { key: uniqueId, ref: combinedRef, ...props, onClick: handleClose, withBackground: withBackground, visible: visible, zIndexBaseValue: zIndexBaseValue, variant: variant, initial: { opacity: 0 }, exit: { opacity: 0, pointerEvents: 'none' }, animate: { opacity: 1 }, transition: Transitions.reduced, withoutModal: withoutModal, inset: inset }))));
});
Backdrop.displayName = 'Backdrop';
export default Backdrop;
const Container = styled(motion.div, shouldNotForwardProps(false, 'zIndexBaseValue', 'withBackground', 'variant', 'visible', 'withoutModal', 'inset'))(({ visible, theme, withBackground, zIndexBaseValue, variant, withoutModal, inset }) => css(css `
      position: fixed;
      inset: 0;
      background-color: ${withBackground && variant === BackdropVariants.default
    ? theme.backdrop.backgroundColor
    : variant === BackdropVariants.medium
        ? theme.backdrop.backgroundColorMedium
        : variant === BackdropVariants.dark
            ? theme.backdrop.backgroundColorDarker
            : 'transparent'};
      pointer-events: ${visible ? 'auto' : 'none'};
      z-index: ${zIndexBaseValue - 1};
    `, withoutModal &&
    css `
        min-block-size: 100vh;
        isolation: isolate;
        ${responsiveStyles({ inset: inset })};
      `));
